import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal,
  Group,
  ActionIcon,
  Center,
  Loader,
  Image,
  Stack,
  Text,
  ScrollArea,
  SimpleGrid,
  rem,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { IconChevronLeft, IconChevronRight, IconExternalLink, IconPhoto, IconVideo, IconLayoutGrid } from '@tabler/icons-react';
import { useGraphService } from '@repo/services';
import {
  DriveItem,
  listFolderItems,
  isImageFile,
  isVideoFile,
  getImagePreviewUrl,
  getSharePointEmbedUrl,
  getSharePointFileUrl,
} from './driveUtils';
import '@mantine/carousel/styles.css';
import { RenameDriveFileButton } from './RenameDriveFileButton';

interface DriveMediaBrowserProps {
  driveId: string;
  folderId?: string | null;
  opened: boolean;
  startItemId?: string | null;
  onClose?: () => void;
  renderItemActions?: (item: DriveItem) => React.ReactNode;
  /** Increment to silently reload the folder (e.g. after metadata was saved) while keeping the current item selected. */
  refreshKey?: number;
  /** Enables filename editing and lets the owning app permission-gate the control. */
  renderRenameControl?: (control: React.ReactNode) => React.ReactNode;
  onFileRenamed?: () => void;
}

/**
 * Displays a full-screen modal carousel for browsing the images and videos in a SharePoint drive folder, with keyboard navigation, a thumbnail strip, and a link to open the current item externally.
 *
 * @param driveId - The SharePoint drive ID to browse.
 * @param folderId - The folder whose image/video children are listed.
 * @param opened - Whether the modal is currently open.
 * @param startItemId - The item ID to initially scroll/slide to when opened.
 * @param onClose - Called when the modal is closed.
 */
export const DriveMediaBrowser: React.FC<DriveMediaBrowserProps> = ({ driveId, folderId, opened, startItemId, onClose, renderItemActions, refreshKey = 0, renderRenameControl, onFileRenamed }) => {
   const { graphClient } = useGraphService();
   const [items, setItems] = useState<DriveItem[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [index, setIndex] = useState<number>(0);
   const [showThumbs, setShowThumbs] = useState(true);
   // carouselRef uses the imperative API from Mantine Carousel (slideTo). Typing is specific to the lib
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
   const carouselRef = React.useRef<any>(null);
   const closeHandler = useCallback(() => { onClose?.(); }, [onClose]);

   const startItemIdRef = React.useRef(startItemId);
   const refreshKeyRef = React.useRef(refreshKey);
   const handledRefreshKeyRef = React.useRef(refreshKey);
   const currentIdRef = React.useRef<string | undefined>(undefined);
   useEffect(() => {
     startItemIdRef.current = startItemId;
     refreshKeyRef.current = refreshKey;
   });

   // Full load whenever the browser opens or its source (client/drive/folder) changes. The cleanup
   // marks the request cancelled, so a slow response for a previous folder can never replace the
   // items of the current one.
   useEffect(() => {
     if (!opened || !graphClient || !driveId || !folderId) return;
     let cancelled = false;
     handledRefreshKeyRef.current = refreshKeyRef.current;
     setItems([]);
     setLoading(true);
     setError(null);
     void (async () => {
       try {
         const list = await listFolderItems(graphClient, driveId, folderId);
         if (cancelled) return;
         const media = list.filter((i) => !i.folder && (isImageFile(i) || isVideoFile(i)));
         setItems(media);
         const idx = startItemIdRef.current ? media.findIndex((m) => m.id === startItemIdRef.current) : -1;
         setIndex(idx >= 0 ? idx : 0);
       } catch (err: unknown) {
         if (cancelled) return;
         setItems([]);
         setError(err instanceof Error ? err.message : String(err || 'Failed to load media'));
       } finally {
         if (!cancelled) setLoading(false);
       }
     })();
     return () => { cancelled = true; };
   }, [opened, graphClient, driveId, folderId]);

   // Silent refresh after metadata saves (refreshKey bump): reload items but keep the same photo
   // selected and keep showing the current list until the new one arrives. Cancelled if the source
   // changes or the viewer closes; ignored when the full load above already covered this key.
   useEffect(() => {
     if (!opened || !graphClient || !driveId || !folderId) return;
     if (refreshKey === handledRefreshKeyRef.current) return;
     handledRefreshKeyRef.current = refreshKey;
     let cancelled = false;
     void (async () => {
       try {
         const list = await listFolderItems(graphClient, driveId, folderId);
         if (cancelled) return;
         const media = list.filter((i) => !i.folder && (isImageFile(i) || isVideoFile(i)));
         setItems(media);
         const keepId = currentIdRef.current;
         const idx = keepId ? media.findIndex((m) => m.id === keepId) : -1;
         if (idx >= 0) setIndex(idx);
         else setIndex((prev) => Math.min(prev, Math.max(media.length - 1, 0)));
       } catch {
         // keep the existing list; the next open reloads
       }
     })();
     return () => {
       cancelled = true;
       // If cancelled before completing, let the next effect run retry this key.
       handledRefreshKeyRef.current = -1;
     };
   }, [refreshKey, opened, graphClient, driveId, folderId]);

   // update index when startItemId changes while opened
   useEffect(() => {
     if (!opened || !startItemId || items.length === 0) return;
     const idx = items.findIndex((m) => m.id === startItemId);
     if (idx >= 0) setIndex(idx);
   }, [startItemId, opened, items]);

   const current = items[index];
   useEffect(() => { currentIdRef.current = current?.id; }, [current?.id]);

   const goPrev = useCallback(() => {
     if (!items.length) return;
     const next = (index - 1 + items.length) % items.length;
     carouselRef.current?.slideTo?.(next);
     setIndex(next);
   }, [index, items.length]);

   const goNext = useCallback(() => {
     if (!items.length) return;
     const next = (index + 1) % items.length;
     carouselRef.current?.slideTo?.(next);
     setIndex(next);
   }, [index, items.length]);

   useEffect(() => {
     const handler = (e: KeyboardEvent) => {
       if (!opened) return;
       if (e.key === 'ArrowLeft') goPrev();
       if (e.key === 'ArrowRight') goNext();
       if (e.key === 'Escape') closeHandler();
     };
     window.addEventListener('keydown', handler);
     return () => window.removeEventListener('keydown', handler);
   }, [opened, closeHandler, goPrev, goNext]);

   // render media inside a Carousel so user can swipe/arrow between items
   const renderCarousel = () => {
     if (!items || items.length === 0) {
       return (
         <Center mih={200}>
           <Text c="dimmed">No media to preview</Text>
         </Center>
       );
     }

     return (
       <Carousel
         initialSlide={index}
         onSlideChange={(val) => setIndex(val)}
         slideSize='100%'
         slideGap='md'
         controlsOffset='xs'
         loop
         align='center'
         nextControlIcon={<IconChevronRight size={16} />}
         previousControlIcon={<IconChevronLeft size={16} />}
         ref={carouselRef as any}
       >
         {items.map((it) => (
           <Carousel.Slide key={it.id}>
             <Center style={{ maxHeight: '100%', overflow: 'hidden' }}>
               {isImageFile(it) ? (
                 <img
                   src={getImagePreviewUrl(it) || getSharePointFileUrl(it) || ''}
                   alt={it.name}
                   style={{
                     maxHeight: '65vh',
                     maxWidth: '100%',
                     width: 'auto',
                     height: 'auto',
                     borderRadius: 8,
                     objectFit: 'contain',
                     display: 'block',
                   }}
                 />
               ) : (
                 <iframe
                   src={getSharePointEmbedUrl(it) || getSharePointFileUrl(it) || ''}
                   title={it.name}
                   style={{ width: '100%', height: '65vh', border: 0, borderRadius: 8 }}
                   allow='autoplay; encrypted-media'
                 />
               )}
             </Center>
           </Carousel.Slide>
         ))}
       </Carousel>
     );
   };

   const thumbs = useMemo(() => items.map((it) => ({ id: it.id, name: it.name, thumb: isImageFile(it) ? (getImagePreviewUrl(it) || '') : (it.thumbnails?.[0]?.medium?.url || it.thumbnails?.[0]?.small?.url || '') })), [items]);

   return (
     <Modal.Root opened={opened} onClose={closeHandler} size='xl' centered style={{ height:'90vh' }}>
       <Modal.Overlay />
       <Modal.Content>
         <Modal.Header>
            <Group gap={4} wrap='nowrap' data-file-name style={{ minWidth: 0 }}>
              <Modal.Title>
                <Text size={'xl'} style={{ overflowWrap: 'anywhere' }}>{current?.name}</Text>
              </Modal.Title>
              {current && renderRenameControl?.(
                <RenameDriveFileButton key={current.id} driveId={driveId} item={current} onRenamed={(id, name) => {
                  setItems((previous) => previous.map((item) => item.id === id ? { ...item, name } : item));
                  onFileRenamed?.();
                }} />
              )}
            </Group>
           <Group justify='space-between' grow>
             <Group align='center' gap={4}>
               {current && (
                 <ActionIcon
                   variant='subtle'
                   onClick={() => {
                     if (getSharePointFileUrl(current)) window.open(getSharePointFileUrl(current)!, '_blank');
                   }}
                   aria-label='Open external'
                 >
                   <IconExternalLink />
                 </ActionIcon>
               )}
               <ActionIcon
                 variant='subtle'
                 onClick={() => setShowThumbs((s) => !s)}
                 aria-label='Toggle thumbnails'
               >
                 <IconLayoutGrid />
               </ActionIcon>
               <Modal.CloseButton onClick={closeHandler} />
             </Group>
           </Group>
         </Modal.Header>
         <Modal.Body>
           <Stack gap='xs'>
             {loading ? (
               <Center mih={220}>
                 <Loader />
               </Center>
             ) : error ? (
               <Center mih={220}>
                 <Text c='red'>{error}</Text>
               </Center>
             ) : (
               <div>
                  {renderCarousel()}
                  {current && renderItemActions?.(current)}
                  {showThumbs && (
                   <ScrollArea style={{ marginTop: rem(12), maxHeight: rem(120) }} type='auto'>
                     <SimpleGrid cols={Math.min(8, thumbs.length || 1)} spacing='xs'>
                       {items.map((it, i) => {
                         const thumbUrl = isImageFile(it)
                           ? getImagePreviewUrl(it) || ''
                           : it.thumbnails?.[0]?.medium?.url || it.thumbnails?.[0]?.small?.url || '';
                         return (
                           <div
                             key={it.id}
                             style={{
                               cursor: 'pointer',
                               padding: 2,
                               border:
                                 i === index
                                   ? '2px solid var(--mantine-color-blue-6)'
                                   : '2px solid transparent',
                               borderRadius: 6
                             }}
                             onClick={() => {
                               if (carouselRef.current?.slideTo) carouselRef.current.slideTo(i);
                               setIndex(i);
                             }}
                           >
                             {thumbUrl ? (
                               <Image
                                 src={thumbUrl}
                                 alt={it.name}
                                 radius={6}
                                 style={{ height: rem(64), objectFit: 'cover' }}
                               />
                             ) : (
                               <Center
                                 style={{
                                   height: rem(64),
                                   border: '1px solid var(--mantine-color-gray-4)',
                                   borderRadius: 6
                                 }}
                               >
                                 {isImageFile(it) ? <IconPhoto size={24} /> : <IconVideo size={24} />}
                               </Center>
                             )}
                           </div>
                         );
                       })}
                     </SimpleGrid>
                   </ScrollArea>
                 )}
               </div>
             )}
           </Stack>
         </Modal.Body>
       </Modal.Content>
     </Modal.Root>
   );
};
